import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  GitMerge,
  ArrowRight,
} from "lucide-react";

const MergeTwoSortedListsVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Equal length lists",
      list1: [1, 2, 4],
      list2: [1, 3, 4],
      expected: [1, 1, 2, 3, 4, 4],
    },
    {
      id: 2,
      name: "Different lengths",
      list1: [1, 3, 5],
      list2: [2, 4, 6, 8],
      expected: [1, 2, 3, 4, 5, 6, 8],
    },
    {
      id: 3,
      name: "One empty list",
      list1: [],
      list2: [1, 2, 3],
      expected: [1, 2, 3],
    },
    {
      id: 4,
      name: "Both empty",
      list1: [],
      list2: [],
      expected: [],
    },
    {
      id: 5,
      name: "Non-overlapping",
      list1: [1, 2, 3],
      list2: [4, 5, 6],
      expected: [1, 2, 3, 4, 5, 6],
    },
    {
      id: 6,
      name: "Single elements",
      list1: [2],
      list2: [1],
      expected: [1, 2],
    },
    {
      id: 7,
      name: "Duplicate values",
      list1: [1, 1, 1],
      list2: [1, 1, 1],
      expected: [1, 1, 1, 1, 1, 1],
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customList1, setCustomList1] = useState("");
  const [customList2, setCustomList2] = useState("");
  const [list1, setList1] = useState(testCases[0].list1);
  const [list2, setList2] = useState(testCases[0].list2);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Algorithm approach
  const [approach, setApproach] = useState("iterative"); // "iterative" or "recursive"

  // Pseudo code for both approaches
  const iterativePseudoCode = [
    { line: "function mergeTwoLists(list1, list2):", indent: 0 },
    { line: "dummy = new ListNode(-1)", indent: 1 },
    { line: "current = dummy", indent: 1 },
    { line: "", indent: 0 },
    { line: "while (list1 && list2):", indent: 1 },
    { line: "if (list1.val <= list2.val):", indent: 2 },
    { line: "current.next = list1", indent: 3 },
    { line: "list1 = list1.next", indent: 3 },
    { line: "else:", indent: 2 },
    { line: "current.next = list2", indent: 3 },
    { line: "list2 = list2.next", indent: 3 },
    { line: "current = current.next", indent: 2 },
    { line: "", indent: 0 },
    { line: "// Attach remaining nodes", indent: 1 },
    { line: "current.next = list1 || list2", indent: 1 },
    { line: "return dummy.next", indent: 1 },
  ];

  const recursivePseudoCode = [
    { line: "function mergeTwoLists(list1, list2):", indent: 0 },
    { line: "// Base cases", indent: 1 },
    { line: "if (!list1) return list2", indent: 1 },
    { line: "if (!list2) return list1", indent: 1 },
    { line: "", indent: 0 },
    { line: "// Compare and recurse", indent: 1 },
    { line: "if (list1.val <= list2.val):", indent: 1 },
    { line: "list1.next = mergeTwoLists(list1.next, list2)", indent: 2 },
    { line: "return list1", indent: 2 },
    { line: "else:", indent: 1 },
    { line: "list2.next = mergeTwoLists(list1, list2.next)", indent: 2 },
    { line: "return list2", indent: 2 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps for iterative approach
  const calculateIterativeSteps = (arr1, arr2) => {
    const allSteps = [];
    let list1Copy = [...arr1];
    let list2Copy = [...arr2];
    let mergedList = [];
    let idx1 = 0;
    let idx2 = 0;
    let step = 0;

    // Initial state
    allSteps.push({
      step: 0,
      list1: arr1,
      list2: arr2,
      mergedList: [],
      idx1: -1,
      idx2: -1,
      currentMergeIdx: -1,
      description: "Initialize: Create dummy node and current pointer",
      highlightLine: 1,
      comparison: null,
    });

    // Merging phase
    while (idx1 < list1Copy.length && idx2 < list2Copy.length) {
      step++;
      const val1 = list1Copy[idx1];
      const val2 = list2Copy[idx2];

      // Show comparison
      allSteps.push({
        step,
        list1: list1Copy,
        list2: list2Copy,
        mergedList: [...mergedList],
        idx1,
        idx2,
        currentMergeIdx: mergedList.length,
        description: `Compare: ${val1} ${val1 <= val2 ? "≤" : ">"} ${val2}`,
        highlightLine: 5,
        comparison: { val1, val2, result: val1 <= val2 },
      });

      // Add to merged list
      if (val1 <= val2) {
        mergedList.push(val1);
        idx1++;

        allSteps.push({
          step: step + 0.5,
          list1: list1Copy,
          list2: list2Copy,
          mergedList: [...mergedList],
          idx1: idx1 - 1,
          idx2,
          currentMergeIdx: mergedList.length - 1,
          description: `Add ${val1} from list1 to merged list`,
          highlightLine: 6,
          addedValue: val1,
          fromList: 1,
        });
      } else {
        mergedList.push(val2);
        idx2++;

        allSteps.push({
          step: step + 0.5,
          list1: list1Copy,
          list2: list2Copy,
          mergedList: [...mergedList],
          idx1,
          idx2: idx2 - 1,
          currentMergeIdx: mergedList.length - 1,
          description: `Add ${val2} from list2 to merged list`,
          highlightLine: 9,
          addedValue: val2,
          fromList: 2,
        });
      }
    }

    // Attach remaining elements
    if (idx1 < list1Copy.length) {
      const remaining = list1Copy.slice(idx1);
      allSteps.push({
        step: step + 1,
        list1: list1Copy,
        list2: list2Copy,
        mergedList: [...mergedList, ...remaining],
        idx1,
        idx2,
        currentMergeIdx: mergedList.length,
        description: `Attach remaining elements from list1: [${remaining.join(
          ", "
        )}]`,
        highlightLine: 14,
        remainingFrom: 1,
      });
      mergedList.push(...remaining);
    } else if (idx2 < list2Copy.length) {
      const remaining = list2Copy.slice(idx2);
      allSteps.push({
        step: step + 1,
        list1: list1Copy,
        list2: list2Copy,
        mergedList: [...mergedList, ...remaining],
        idx1,
        idx2,
        currentMergeIdx: mergedList.length,
        description: `Attach remaining elements from list2: [${remaining.join(
          ", "
        )}]`,
        highlightLine: 14,
        remainingFrom: 2,
      });
      mergedList.push(...remaining);
    }

    // Final state
    allSteps.push({
      step: step + 2,
      list1: list1Copy,
      list2: list2Copy,
      mergedList: mergedList,
      idx1: -1,
      idx2: -1,
      currentMergeIdx: -1,
      description: `Merge complete! Final list: [${mergedList.join(", ")}]`,
      highlightLine: 15,
      isFinal: true,
    });

    return allSteps;
  };

  // Initialize steps when test case changes
  useEffect(() => {
    const newSteps =
      approach === "iterative"
        ? calculateIterativeSteps(list1, list2)
        : calculateIterativeSteps(list1, list2); // For now, using iterative for both
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
  }, [list1, list2, approach]);

  // Auto-play functionality
  useEffect(() => {
    if (!isPlaying || finished) return;

    const timer = setTimeout(() => {
      handleNextStep();
    }, speed);

    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      const step = steps[nextStep];
      setHighlightedLine(step.highlightLine);

      if (step.isFinal) {
        setFinished(true);
        setIsPlaying(false);
      }
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      const step = steps[prevStep];
      setHighlightedLine(step.highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setList1(testCases[index].list1);
    setList2(testCases[index].list2);
  };

  const addCustomTestCase = () => {
    try {
      const cleanList1 = customList1.trim().replace(/[\[\]]/g, "");
      const cleanList2 = customList2.trim().replace(/[\[\]]/g, "");

      const nums1 = cleanList1
        ? cleanList1.split(",").map((n) => parseInt(n.trim()))
        : [];
      const nums2 = cleanList2
        ? cleanList2.split(",").map((n) => parseInt(n.trim()))
        : [];

      if (
        (nums1.length > 0 && nums1.some(isNaN)) ||
        (nums2.length > 0 && nums2.some(isNaN))
      ) {
        alert("Please enter valid numbers");
        return;
      }

      // Check if lists are sorted
      const isSorted = (arr) => {
        for (let i = 1; i < arr.length; i++) {
          if (arr[i] < arr[i - 1]) return false;
        }
        return true;
      };

      if (!isSorted(nums1) || !isSorted(nums2)) {
        alert("Both lists must be sorted in ascending order");
        return;
      }

      const merged = [...nums1, ...nums2].sort((a, b) => a - b);
      const newTestCase = {
        id: Date.now(),
        name: `Custom ${testCases.length - 6}`,
        list1: nums1,
        list2: nums2,
        expected: merged,
      };

      setTestCases([...testCases, newTestCase]);
      setCustomList1("");
      setCustomList2("");
    } catch (error) {
      alert("Invalid input format");
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      const newTestCases = testCases.filter((tc) => tc.id !== id);
      setTestCases(newTestCases);
      if (selectedTestCase >= newTestCases.length) {
        handleTestCaseChange(0);
      }
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <GitMerge className="text-blue-500" size={24} />
        Merge Two Sorted Lists
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left side - Visualization */}
        <div className="space-y-3">
          {/* Approach Selector */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Select Approach</h3>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  setApproach("iterative");
                  reset();
                }}
                className={`px-3 py-1.5 text-xs rounded transition-colors ${
                  approach === "iterative"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
              >
                Iterative
              </button>
              <button
                onClick={() => {
                  setApproach("recursive");
                  reset();
                }}
                className={`px-3 py-1.5 text-xs rounded transition-colors ${
                  approach === "recursive"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
              >
                Recursive
              </button>
            </div>
          </div>

          {/* Test Cases */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-36 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">
                      L1: [{tc.list1.join(", ")}], L2: [{tc.list2.join(", ")}]
                    </div>
                    <div className="text-xs opacity-75">
                      Result: [{tc.expected.join(", ")}]
                    </div>
                  </button>
                  {tc.id > 7 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Add custom test case */}
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customList1}
                onChange={(e) => setCustomList1(e.target.value)}
                placeholder="List 1: 1,2,4"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <input
                type="text"
                value={customList2}
                onChange={(e) => setCustomList2(e.target.value)}
                placeholder="List 2: 1,3,4"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} />
                Add Test Case
              </button>
            </div>
          </div>

          {/* Lists Visualization */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            {/* Comparison Display */}
            {currentStepData?.comparison && (
              <div className="mb-3 p-2 bg-yellow-50 rounded">
                <p className="text-xs text-yellow-800 font-medium">
                  Comparing: {currentStepData.comparison.val1}{" "}
                  {currentStepData.comparison.result ? "≤" : ">"}{" "}
                  {currentStepData.comparison.val2}
                </p>
              </div>
            )}

            {/* Lists Visual */}
            <div className="space-y-3">
              {/* List 1 */}
              <div>
                <p className="text-xs font-medium text-gray-700 mb-1">
                  List 1:
                </p>
                <div className="flex gap-1 flex-wrap">
                  {currentStepData?.list1.map((val, idx) => (
                    <div
                      key={idx}
                      className={`w-10 h-10 flex items-center justify-center border-2 rounded transition-all duration-300 ${
                        idx === currentStepData.idx1
                          ? "bg-blue-200 border-blue-500 scale-110"
                          : idx < (currentStepData.idx1 || 0)
                          ? "bg-gray-200 border-gray-400 opacity-50"
                          : "bg-gray-100 border-gray-300"
                      }`}
                    >
                      <span className="font-mono text-sm font-bold">{val}</span>
                    </div>
                  ))}
                  {currentStepData?.list1.length === 0 && (
                    <span className="text-xs text-gray-400 italic">Empty</span>
                  )}
                </div>
              </div>

              {/* List 2 */}
              <div>
                <p className="text-xs font-medium text-gray-700 mb-1">
                  List 2:
                </p>
                <div className="flex gap-1 flex-wrap">
                  {currentStepData?.list2.map((val, idx) => (
                    <div
                      key={idx}
                      className={`w-10 h-10 flex items-center justify-center border-2 rounded transition-all duration-300 ${
                        idx === currentStepData.idx2
                          ? "bg-green-200 border-green-500 scale-110"
                          : idx < (currentStepData.idx2 || 0)
                          ? "bg-gray-200 border-gray-400 opacity-50"
                          : "bg-gray-100 border-gray-300"
                      }`}
                    >
                      <span className="font-mono text-sm font-bold">{val}</span>
                    </div>
                  ))}
                  {currentStepData?.list2.length === 0 && (
                    <span className="text-xs text-gray-400 italic">Empty</span>
                  )}
                </div>
              </div>

              {/* Arrow indicator for movement */}
              {currentStepData?.addedValue !== undefined && (
                <div className="flex justify-center">
                  <div className="text-gray-400">
                    <ArrowRight size={20} className="transform rotate-90" />
                  </div>
                </div>
              )}

              {/* Merged List */}
              <div>
                <p className="text-xs font-medium text-gray-700 mb-1">
                  Merged List:
                </p>
                <div className="flex gap-1 flex-wrap">
                  {currentStepData?.mergedList.map((val, idx) => (
                    <div
                      key={idx}
                      className={`w-10 h-10 flex items-center justify-center border-2 rounded transition-all duration-300 ${
                        idx === currentStepData.currentMergeIdx
                          ? "bg-purple-200 border-purple-500 scale-110 animate-pulse"
                          : idx === currentStepData.mergedList.length - 1 &&
                            currentStepData.addedValue === val
                          ? "bg-yellow-200 border-yellow-500"
                          : "bg-purple-100 border-purple-300"
                      }`}
                    >
                      <span className="font-mono text-sm font-bold">{val}</span>
                    </div>
                  ))}
                  {currentStepData?.mergedList.length === 0 && (
                    <span className="text-xs text-gray-400 italic">Empty</span>
                  )}
                </div>
              </div>
            </div>

            {/* Result */}
            {currentStepData?.isFinal && (
              <div className="mt-3 p-3 bg-green-100 rounded">
                <h4 className="text-sm font-bold text-green-900">
                  Merge Complete!
                </h4>
                <p className="text-xs text-green-700 mt-1">
                  Final merged list: [{currentStepData.mergedList.join(", ")}]
                </p>
                <p className="text-xs text-green-700 mt-1">
                  Total elements: {currentStepData.mergedList.length}
                </p>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep === 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>

              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                {isPlaying ? "Pause" : "Play"}
              </button>

              <button
                onClick={handleNextStep}
                disabled={currentStep === steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>

              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} />
                Reset
              </button>

              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="2000"
                  step="200"
                  value={2200 - speed}
                  onChange={(e) => setSpeed(2200 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>

            {/* Step indicator */}
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right side - Pseudo Code & Analysis */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">
              {approach === "iterative"
                ? "Iterative Algorithm"
                : "Recursive Algorithm"}
            </h3>
            <div className="space-y-0.5">
              {(approach === "iterative"
                ? iterativePseudoCode
                : recursivePseudoCode
              ).map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>

          {/* Complexity Analysis */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Complexity Analysis</h3>
            <div className="grid grid-cols-2 gap-3">
              <div
                className={`p-2 rounded ${
                  approach === "iterative" ? "bg-blue-50" : "bg-gray-50"
                }`}
              >
                <h4 className="text-xs font-medium text-gray-700 mb-1">
                  Iterative
                </h4>
                <div className="space-y-1 text-xs">
                  <div>
                    <span className="font-medium">Time:</span> O(n + m)
                  </div>
                  <div>
                    <span className="font-medium">Space:</span> O(1)
                  </div>
                </div>
              </div>
              <div
                className={`p-2 rounded ${
                  approach === "recursive" ? "bg-blue-50" : "bg-gray-50"
                }`}
              >
                <h4 className="text-xs font-medium text-gray-700 mb-1">
                  Recursive
                </h4>
                <div className="space-y-1 text-xs">
                  <div>
                    <span className="font-medium">Time:</span> O(n + m)
                  </div>
                  <div>
                    <span className="font-medium">Space:</span> O(n + m)
                  </div>
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              n = length of list1, m = length of list2
            </p>
          </div>

          {/* Algorithm Explanation */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">How It Works</h3>
            {approach === "iterative" ? (
              <ol className="space-y-1 text-xs text-gray-700">
                <li className="flex">
                  <span className="font-medium mr-1">1.</span>
                  <span>Create a dummy node to simplify edge cases</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">2.</span>
                  <span>Use a current pointer to build the merged list</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">3.</span>
                  <span>Compare heads of both lists</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">4.</span>
                  <span>Attach the smaller node to merged list</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">5.</span>
                  <span>Move the pointer of the chosen list forward</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">6.</span>
                  <span>Attach remaining nodes when one list is exhausted</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">7.</span>
                  <span>Return dummy.next as the merged list head</span>
                </li>
              </ol>
            ) : (
              <ol className="space-y-1 text-xs text-gray-700">
                <li className="flex">
                  <span className="font-medium mr-1">1.</span>
                  <span>
                    Base case: if either list is null, return the other
                  </span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">2.</span>
                  <span>Compare the values at heads of both lists</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">3.</span>
                  <span>Choose the smaller value as the current node</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">4.</span>
                  <span>Recursively merge the rest</span>
                </li>
                <li className="flex">
                  <span className="font-medium mr-1">5.</span>
                  <span>Connect the current node to the merged result</span>
                </li>
              </ol>
            )}
          </div>

          {/* Key Insights */}
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Both lists are already sorted, so we only compare heads
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>Dummy node eliminates edge case handling</span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  No need to check remaining elements - they're already sorted
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The merge is stable - equal elements maintain relative order
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>Similar to merge step in merge sort algorithm</span>
              </li>
            </ul>
          </div>

          {/* Visual Guide */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-200 border-2 border-blue-500 rounded"></div>
                <span>Current element in List 1</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-green-200 border-2 border-green-500 rounded"></div>
                <span>Current element in List 2</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-purple-100 border-2 border-purple-300 rounded"></div>
                <span>Elements in merged list</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-yellow-200 border-2 border-yellow-500 rounded"></div>
                <span>Just added to merged list</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gray-200 border-2 border-gray-400 rounded opacity-50"></div>
                <span>Already processed</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MergeTwoSortedListsVisualizer;
